const clone = (value) => JSON.parse(JSON.stringify(value));
const JERUSALEM_TIME_ZONE = "Asia/Jerusalem";

export const resolveAutopilotAdminPackageSelection = ({ assignment, rsvpAccess } = {}) => (
  assignment?.packageId
  || (rsvpAccess?.classification === "FREE" ? "__free__" : "")
  || (rsvpAccess == null || rsvpAccess?.classification === "MANUAL" ? "__manual__" : "")
  || ""
);

export const retainAutopilotAdminPackageSelection = ({ current, templates = [] } = {}) => (
  ["__free__", "__manual__", "__remove__"].includes(current)
  || templates.some((template) => template.id === current)
    ? current
    : ""
);

const jerusalemParts = (value) => Object.fromEntries(
  new Intl.DateTimeFormat("en-CA", {
    timeZone: JERUSALEM_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value))
    .filter(({ type }) => type !== "literal")
    .map(({ type, value: part }) => [type, part])
);

export const formatJerusalemDateTimeLocal = (value) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const parts = jerusalemParts(date);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

export const parseJerusalemDateTimeLocal = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) throw new Error("FUTURE_STEP_TIME_INVALID");
  const [, year, month, day, hour, minute] = match.map(Number);
  const requestedUtc = Date.UTC(year, month - 1, day, hour, minute);
  let instant = requestedUtc;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = jerusalemParts(instant);
    const representedUtc = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour), Number(parts.minute), Number(parts.second),
    );
    instant -= representedUtc - requestedUtc;
  }
  const result = new Date(instant);
  if (formatJerusalemDateTimeLocal(result) !== value) throw new Error("FUTURE_STEP_TIME_INVALID");
  return result.toISOString();
};

const jerusalemMetadata = (value) => {
  const date = new Date(value);
  const parts = jerusalemParts(date);
  const localDate = `${parts.year}-${parts.month}-${parts.day}`;
  const localTime = `${parts.hour}:${parts.minute}`;
  const hebrewWeekday = new Intl.DateTimeFormat("he-IL", {
    timeZone: JERUSALEM_TIME_ZONE,
    weekday: "long",
  }).format(date);
  return {
    timeZone: JERUSALEM_TIME_ZONE,
    localDate,
    localTime,
    hebrewWeekday,
    hebrewDisplay: `${hebrewWeekday}, ${parts.day}.${parts.month}.${parts.year}, ${localTime}`,
  };
};

export const buildPackageAssignmentPatch = ({ currentData = {}, template, recordLimit, actor, now = new Date() }) => {
  const parsedLimit = Number(recordLimit);
  if (!template?.id || !template?.name || !Number.isInteger(template.revision) || !Array.isArray(template.steps)) {
    throw new Error("PACKAGE_TEMPLATE_INVALID");
  }
  if (!Number.isInteger(parsedLimit) || parsedLimit < 1 || parsedLimit > 1000000) {
    throw new Error("RECORD_LIMIT_INVALID");
  }
  const previousRevision = Number(currentData.autopilotAssignment?.assignmentRevision || 0);
  const patch = {
    rsvpAccess: {
      schemaVersion: 1,
      classification: "AUTOPILOT",
      updatedAt: new Date(now).toISOString(),
      updatedBy: {
        uid: String(actor?.uid || ""),
        email: String(actor?.email || "").toLowerCase(),
        authorization: "existing-event-owner-ctrl-567",
      },
    },
    autopilotAssignment: {
      packageId: template.id,
      packageName: template.name,
      packageRevision: template.revision,
      assignmentRevision: previousRevision + 1,
      assignedAt: new Date(now).toISOString(),
      assignedBy: {
        uid: String(actor?.uid || ""),
        email: String(actor?.email || "").toLowerCase(),
        authorization: "existing-event-owner-ctrl-567",
      },
      templateSnapshot: clone(template),
    },
    recordLimit: parsedLimit,
  };
  const hasCompletedStep = currentData.autopilotPlan?.steps?.some((step) => step.executionStatus === "completed" || step.status === "completed") === true;
  if ((!currentData.autopilotLifecycle || currentData.autopilotLifecycle.status === "inactive") && !hasCompletedStep) {
    patch.autopilotLifecycle = {
      status: "inactive",
      activationTime: null,
      stoppedAt: null,
      lastResumedAt: null,
      planRevision: 0,
    };
    patch.autopilotPlan = null;
  }
  return patch;
};

export const buildRsvpAccessTransitionPatch = ({ currentData = {}, classification, deleteFieldValue, actor, now = new Date() }) => {
  if (!["FREE", "MANUAL"].includes(classification)) throw new Error("RSVP_ACCESS_CLASSIFICATION_INVALID");
  if (deleteFieldValue === undefined) throw new Error("DELETE_FIELD_VALUE_REQUIRED");
  const changedAt = new Date(now).toISOString();
  const changedBy = {
    uid: String(actor?.uid || ""),
    email: String(actor?.email || "").toLowerCase(),
    authorization: "existing-event-owner-ctrl-567",
  };
  const history = Array.isArray(currentData.autopilotPlanHistory)
    ? clone(currentData.autopilotPlanHistory)
    : [];
  if (currentData.autopilotPlan) {
    history.push({
      accessTransitionAt: changedAt,
      accessTransitionBy: changedBy,
      activationTime: currentData.autopilotLifecycle?.activationTime || currentData.autopilotPlan.activationTime || null,
      planRevision: Number(currentData.autopilotLifecycle?.planRevision || currentData.autopilotPlan.planRevision || 0),
      plan: clone(currentData.autopilotPlan),
    });
  }
  return {
    rsvpAccess: {
      schemaVersion: 1,
      classification,
      ...(classification === "MANUAL" ? { allowance: { mode: "UNLIMITED" } } : {}),
      updatedAt: changedAt,
      updatedBy: changedBy,
    },
    ...Object.fromEntries(AUTOPILOT_PACKAGE_STATE_FIELDS.map((field) => [field, deleteFieldValue])),
    ...(currentData.autopilotPlan ? { autopilotPlanHistory: history } : {}),
  };
};

export const buildAutopilotResetPatch = ({ currentData = {}, actor, now = new Date() }) => {
  const resetAt = new Date(now).toISOString();
  const resetBy = {
    uid: String(actor?.uid || ""),
    email: String(actor?.email || "").toLowerCase(),
    authorization: "existing-event-owner-ctrl-567",
  };
  const previousPlanHistory = Array.isArray(currentData.autopilotPlanHistory)
    ? clone(currentData.autopilotPlanHistory)
    : [];

  if (currentData.autopilotPlan) {
    previousPlanHistory.push({
      resetAt,
      resetBy,
      activationTime: currentData.autopilotLifecycle?.activationTime || currentData.autopilotPlan.activationTime || null,
      planRevision: Number(currentData.autopilotLifecycle?.planRevision || currentData.autopilotPlan.planRevision || 0),
      plan: clone(currentData.autopilotPlan),
    });
  }

  return {
    autopilotLifecycle: {
      status: "inactive",
      activationTime: null,
      stoppedAt: null,
      lastResumedAt: null,
      planRevision: 0,
      lastResetAt: resetAt,
      lastResetBy: resetBy,
    },
    autopilotPlan: null,
    ...(currentData.autopilotPlan ? { autopilotPlanHistory: previousPlanHistory } : {}),
  };
};

export const AUTOPILOT_PACKAGE_STATE_FIELDS = Object.freeze([
  "autopilotAssignment",
  "recordLimit",
  "recordLimitEnforced",
  "autopilotLifecycle",
  "autopilotPlan",
]);

export const buildAutopilotPackageRemovalPatch = (deleteFieldValue) => {
  if (deleteFieldValue === undefined) throw new Error("DELETE_FIELD_VALUE_REQUIRED");
  return Object.fromEntries(
    AUTOPILOT_PACKAGE_STATE_FIELDS.map((field) => [field, deleteFieldValue])
  );
};

const isCompleted = (step) => step.executionStatus === "completed" || step.status === "completed";
// Gift configuration attention is raised before any queue job exists; rescheduling is its intended fix.
const OWNER_FIXABLE_ATTENTION = new Set(["GIFT_LINK_INVALID", "GIFT_WHATSAPP_TEMPLATE_MISSING"]);
const STARTED_EDITABLE_FIELDS = ["scheduledAt", "channel", "audience", "executionStatus"];
// A started Step may already have reached the provider. Rescheduling it would mint a new
// execution under a new plan revision and resend the whole round, so it is frozen like a completed Step.
export const isStartedStep = (step) => {
  const status = step?.executionStatus || step?.status;
  if (!step?.execution?.id || isCompleted(step) || status === "failed") return false;
  return !(status === "requiresAttention" && OWNER_FIXABLE_ATTENTION.has(step.requiresAttentionReason)
    && Object.keys(step.execution.operations || {}).length === 0);
};
// A cutoff-held Gift has no executable time; it must not invalidate other edits.
const isPaused = (step) => (step.executionStatus || step.status) === "paused"
  || (step.kind === "giftLink" && step.executionStatus === "requiresAttention" && ["GIFT_SCHEDULING_CUTOFF", "GIFT_SCHEDULE_INVALID"].includes(step.requiresAttentionReason));

export const getFuturePlanEditWarnings = ({ draftSteps = [] } = {}) => {
  const warnings = [];
  const activeChronological = [...draftSteps]
    .filter((step) => !isCompleted(step) && !isPaused(step))
    .sort((left, right) => new Date(left.scheduledAt) - new Date(right.scheduledAt));
  for (let index = 1; index < activeChronological.length; index += 1) {
    const previous = activeChronological[index - 1];
    const current = activeChronological[index];
    const previousTime = new Date(previous.scheduledAt).getTime();
    const currentTime = new Date(current.scheduledAt).getTime();
    if (!Number.isFinite(previousTime) || !Number.isFinite(currentTime)) continue;
    if (currentTime - previousTime < 4 * 60 * 60 * 1000) {
      warnings.push({
        code: "AUTOPILOT_STEP_SPACING_RECOMMENDED",
        previousStepId: previous.id,
        stepId: current.id,
      });
    }
  }
  return warnings;
};

// Evaluate the visible times, never historical automatic-preview flags.
export const getCurrentPlanScheduleWarnings = ({ draftSteps = [] } = {}) => {
  const active = draftSteps.filter(item => !isCompleted(item) && !isPaused(item) && (item.executionStatus || item.status) !== "failed");
  const warnings = getFuturePlanEditWarnings({ draftSteps: active });
  for (const step of active) {
    const local = formatJerusalemDateTimeLocal(step.scheduledAt);
    if (!local) continue;
    const [date, time] = local.split("T");
    const weekday = new Date(`${date}T12:00:00Z`).getUTCDay();
    if (time < "08:30" || time > "21:30" || (weekday === 5 && time > "15:00") || (weekday === 6 && time < "20:30")) {
      warnings.push({ code: "EXCEPTIONAL_SENDING_HOURS", stepId: step.id });
    }
  }
  return warnings;
};

const throwPlanValidationError = (code, invalidStepIds = []) => {
  const error = new Error(code);
  error.code = code;
  if (invalidStepIds.length > 0) error.details = { invalidStepIds: [...new Set(invalidStepIds.filter(Boolean))] };
  throw error;
};

export const buildFuturePlanEditPatch = ({ currentPlan, lifecycle, draftSteps, now = new Date(), allowStructuralEdits = false }) => {
  if (!currentPlan || !Array.isArray(currentPlan.steps) || !Array.isArray(draftSteps)) {
    throwPlanValidationError("AUTOPILOT_PLAN_INVALID");
  }
  const currentCompleted = currentPlan.steps.filter(isCompleted);
  for (const completed of currentCompleted) {
    const draft = draftSteps.find((step) => step.id === completed.id);
    if (!draft || JSON.stringify(draft) !== JSON.stringify(completed)) throwPlanValidationError("COMPLETED_STEP_IMMUTABLE", [completed.id]);
  }
  // Started Steps keep the saved copy; their delivery counters may be newer than the draft.
  const currentStarted = new Map(currentPlan.steps.filter(isStartedStep).map((step) => [step.id, step]));
  for (const started of currentStarted.values()) {
    const draft = draftSteps.find((step) => step.id === started.id);
    if (!draft || STARTED_EDITABLE_FIELDS.some((field) => JSON.stringify(draft[field]) !== JSON.stringify(started[field]))) {
      throwPlanValidationError("STARTED_STEP_IMMUTABLE", [started.id]);
    }
  }
  if (!allowStructuralEdits) {
    const currentIds = currentPlan.steps.map((step) => step.id).sort();
    const draftIds = draftSteps.map((step) => step.id).sort();
    if (JSON.stringify(currentIds) !== JSON.stringify(draftIds)) throwPlanValidationError("AUTOPILOT_STEP_STRUCTURE_IMMUTABLE");
  }
  if (new Set(draftSteps.map((step) => step.id)).size !== draftSteps.length) throwPlanValidationError("AUTOPILOT_STEP_ID_DUPLICATE");
  const editTime = new Date(now);
  const future = draftSteps.filter((step) => !isCompleted(step) && !currentStarted.has(step.id));
  for (const step of future) {
    const scheduled = new Date(step.scheduledAt);
    const paused = isPaused(step);
    if (!step.id || !Number.isFinite(scheduled.getTime()) || (!paused && scheduled <= editTime)) throwPlanValidationError("FUTURE_STEP_TIME_INVALID", [step.id]);
    if (step.kind === "voice" && step.channel !== "voice") throwPlanValidationError("AUTOPILOT_STEP_CHANNEL_INVALID", [step.id]);
    if (step.kind === "thankYou" && step.channel !== "sms") throwPlanValidationError("AUTOPILOT_STEP_CHANNEL_INVALID", [step.id]);
    if (step.kind === "message" && !["sms", "whatsapp"].includes(step.channel)) throwPlanValidationError("AUTOPILOT_STEP_CHANNEL_INVALID", [step.id]);
    if (step.kind === "giftLink" && !["sms", "whatsapp"].includes(step.channel)) throwPlanValidationError("AUTOPILOT_STEP_CHANNEL_INVALID", [step.id]);
    if (step.kind === "giftLink" && !["all", "approvedAndPending", "approved"].includes(step.audience || "all")) throwPlanValidationError("AUTOPILOT_GIFT_AUDIENCE_INVALID", [step.id]);
  }
  const byId = new Map(draftSteps.map((step) => [step.id, step]));
  if (!allowStructuralEdits) {
    const ordered = currentPlan.steps.map((step) => byId.get(step.id)).filter((step) => step && !isPaused(step));
    for (let index = 1; index < ordered.length; index += 1) {
      const previousTime = new Date(ordered[index - 1].scheduledAt).getTime();
      const currentTime = new Date(ordered[index].scheduledAt).getTime();
      if (currentTime < previousTime) {
        throwPlanValidationError("AUTOPILOT_STEP_ORDER_INVALID", [ordered[index].id]);
      }
      // Close spacing is informational; chronological order remains mandatory.
    }
  }
  const eventTime = new Date(currentPlan.event?.startsAt).getTime();
  if (Number.isFinite(eventTime)) {
    for (const step of future.filter((item) => !isPaused(item))) {
      const scheduled = new Date(step.scheduledAt).getTime();
      if (["message", "voice", "giftLink"].includes(step.kind) && scheduled >= eventTime) throwPlanValidationError("AUTOPILOT_STEP_EVENT_BOUNDARY_INVALID", [step.id]);
      if (step.kind === "thankYou" && (scheduled < eventTime || scheduled > eventTime + (30 * 86400000))) {
        throwPlanValidationError("AUTOPILOT_STEP_EVENT_BOUNDARY_INVALID", [step.id]);
      }
    }
  }
  const invitationTimes = draftSteps
    .filter((step) => !isPaused(step) && step.messagePurpose === "invitation")
    .map((step) => new Date(step.scheduledAt).getTime());
  for (const reminder of draftSteps.filter((step) => !isPaused(step) && step.messagePurpose === "unansweredReminder")) {
    if (!invitationTimes.some((time) => time < new Date(reminder.scheduledAt).getTime())) throwPlanValidationError("AUTOPILOT_REMINDER_ORDER_INVALID", [reminder.id]);
  }
  // Rounds started before execution.planRevision existed keep the revision that claimed them,
  // recorded here before this save increments it.
  const startedSteps = [...currentStarted.values()].map((step) => step.execution.planRevision ? step
    : { ...step, execution: { ...step.execution, planRevision: currentPlan.planRevision || 1 } });
  const steps = [...currentCompleted, ...startedSteps, ...future]
    .sort((left, right) => new Date(left.scheduledAt) - new Date(right.scheduledAt))
    .map((step, index) => isCompleted(step) || currentStarted.has(step.id) ? clone(step) : ({
      ...clone(step),
      order: index + 1,
      ...jerusalemMetadata(step.scheduledAt),
    }));
  const planRevision = Number(lifecycle?.planRevision || currentPlan.planRevision || 1) + 1;
  return {
    autopilotPlan: {
      ...clone(currentPlan),
      steps,
      planRevision,
      adminEditedAt: editTime.toISOString(),
    },
    autopilotLifecycle: {
      ...clone(lifecycle),
      planRevision,
    },
  };
};

// Schedule edit history kept on the event so a later question ("who moved this round, and
// from which screen?") can be answered. Written by the browser in the same save, so it records
// only saves made through these screens; a gap in revisions means a change made elsewhere.
export const PLAN_EDIT_HISTORY_LIMIT = 25;
const PLAN_EDIT_TRACKED_FIELDS = ["scheduledAt", "status", "channel", "messagePurpose", "kind", "audience"];
const planEditView = (step) => Object.fromEntries(PLAN_EDIT_TRACKED_FIELDS.map((field) => [field,
  field === "status" ? (step.executionStatus || step.status || null) : (step[field] ?? null)]));

export const buildPlanEditHistory = ({ beforePlan, afterPlan, previousEdits, source }) => {
  const before = new Map((beforePlan?.steps || []).map((step) => [step.id, planEditView(step)]));
  const after = new Map((afterPlan?.steps || []).map((step) => [step.id, planEditView(step)]));
  const changes = [];
  for (const [stepId, view] of before) {
    if (!after.has(stepId)) { changes.push({ stepId, change: "removed", before: view, after: null }); continue; }
    const next = after.get(stepId);
    const fields = PLAN_EDIT_TRACKED_FIELDS.filter((field) => view[field] !== next[field]);
    if (fields.length > 0) {
      changes.push({ stepId, change: "updated",
        before: Object.fromEntries(fields.map((field) => [field, view[field]])),
        after: Object.fromEntries(fields.map((field) => [field, next[field]])) });
    }
  }
  for (const [stepId, view] of after) {
    if (!before.has(stepId)) changes.push({ stepId, change: "added", before: null, after: view });
  }
  const entry = {
    revision: afterPlan?.planRevision ?? null,
    previousRevision: beforePlan?.planRevision ?? null,
    at: afterPlan?.adminEditedAt ?? null,
    source: source || "unknown",
    changes,
  };
  return [...(Array.isArray(previousEdits) ? previousEdits : []), entry].slice(-PLAN_EDIT_HISTORY_LIMIT);
};
